import { FC, ReactNode, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { Outlet } from 'react-router-dom';
import Header from './Header';
import Sidebar, { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from './Sidebar';
import { TourProvider } from 'src/components/Tour';

interface SidebarLayoutProps {
  children?: ReactNode;
}

const Shell = styled(Box)(
  () => `
    display: flex;
    min-height: 100vh;
    width: 100%;
`
);

const MainColumn = styled(Box)(
  () => `
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    transition: margin-left .22s cubic-bezier(.4,0,.2,1);
`
);

const MainContent = styled(Box)(
  () => `
    flex: 1 1 auto;
    min-width: 0;
    padding: 0px !important;
`
);

const ShellStyles = () => (
  <style>{`
    .kshell{
      background:linear-gradient(135deg,#fcdfe9 0%,#fdeadd 36%,#efe8fb 70%,#f5f1fd 100%);
      transition:background .25s ease;
    }
    .kshell-header{
      height:64px;position:sticky;top:0;z-index:6;
      display:flex;align-items:center;justify-content:space-between;
      padding:0 24px;
      background:rgba(255,255,255,.78);
      backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);
      border-bottom:1px solid rgba(21,18,58,.08);
      font-family:'Inter',sans-serif;
    }
    .kshell-header-left{display:flex;align-items:center;gap:14px;}
    .kshell-header-right{display:flex;align-items:center;gap:10px;}
    .kshell-userbox .MuiButton-root{text-transform:none;border-radius:12px;}
    .kshell-userbox .MuiTypography-root{color:#15123a;}
    .kshell-pagetitle{
      font-family:'Inter',sans-serif;font-size:18px;font-weight:700;
      color:#15123a;margin:0;letter-spacing:-.2px;
    }
    .kshell-iconbtn{
      display:grid;place-items:center;width:38px;height:38px;border-radius:11px;
      border:1px solid rgba(21,18,58,.1);background:#fff;color:#54507a;cursor:pointer;
      transition:all .16s ease;
    }
    .kshell-iconbtn:hover{background:rgba(108,92,231,.08);color:#6c5ce7;border-color:rgba(108,92,231,.3);}
    .kshell-iconbtn svg{font-size:20px;}

    /* ---- professional dark mode: shell chrome ----
       (page surfaces live in style.css under html.dark) */
    html.dark .kshell{
      background:linear-gradient(140deg,#0e0f14 0%,#13141d 55%,#0e0f14 100%) !important;
    }
    html.dark .kshell-header{
      background:rgba(17,18,26,.82) !important;
      border-bottom-color:rgba(255,255,255,.07) !important;
    }
    html.dark .kshell-pagetitle{ color:#eef0f6 !important; }
    html.dark .kshell-iconbtn{
      background:rgba(255,255,255,.05);border-color:rgba(255,255,255,.12);color:rgba(255,255,255,.82);
    }
    html.dark .kshell-iconbtn svg{ color:rgba(255,255,255,.85) !important;fill:rgba(255,255,255,.85) !important; }
    html.dark .kshell-iconbtn:hover{ background:rgba(139,123,240,.2);color:#fff;border-color:rgba(139,123,240,.45); }
    html.dark .kshell-iconbtn:hover svg{ color:#fff !important;fill:#fff !important; }
    html.dark .kshell-userbox .MuiTypography-root{ color:rgba(255,255,255,.88) !important; }
    html.dark .kshell-userbox svg{ color:rgba(255,255,255,.7); }

    /* mobile drawer backdrop */
    .kside-backdrop{
      position:fixed;inset:0;z-index:25;background:rgba(15,11,40,.45);
      -webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px);
      opacity:0;pointer-events:none;transition:opacity .22s ease;
    }
    .kside-backdrop.open{opacity:1;pointer-events:auto;}
    @media (min-width:901px){ .kside-backdrop{display:none;} }
  `}</style>
);

const SidebarLayout: FC<SidebarLayoutProps> = () => {
  // Restore the sidebar expand/collapse preference so it survives a refresh
  // (persisted below, alongside dark mode which is stored as `uiMode`).
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sidebarCollapsed') === 'true'
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dark, setDark] = useState(
    () => localStorage.getItem('uiMode') === 'dark'
  );

  // Persist the sidebar state on every change (covers the hamburger toggle and
  // the "[" / "]" keyboard shortcuts, which all go through setCollapsed).
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', collapsed ? 'true' : 'false');
  }, [collapsed]);

  // Professional dark mode: toggle a class on <html> so the curated dark
  // theme covers every page, portal, toast and dialog. The cleanup removes
  // it when the dashboard layout unmounts (e.g. on logout), so auth pages
  // stay on the light theme.
  useEffect(() => {
    const root = document.documentElement;
    if (dark) root.classList.add('dark');
    else root.classList.remove('dark');
    return () => {
      document.documentElement.classList.remove('dark');
    };
  }, [dark]);

  // Keyboard shortcuts: "]" expands the sidebar, "[" collapses it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        (el && el.isContentEditable)
      ) {
        return;
      }
      if (e.key === ']') setCollapsed(false);
      else if (e.key === '[') setCollapsed(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // On mobile the hamburger opens/closes the drawer; on desktop it collapses.
  const handleToggle = () => {
    if (typeof window !== 'undefined' && window.innerWidth <= 900) {
      setMobileOpen((o) => !o);
    } else {
      setCollapsed((c) => !c);
    }
  };

  const toggleTheme = () => {
    setDark((d) => {
      const next = !d;
      localStorage.setItem('uiMode', next ? 'dark' : 'light');
      return next;
    });
  };

  const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  return (
    // TourProvider wraps the whole shell so the header's tour button, the
    // once-per-login prompt and the spotlight overlay all share one state.
    <TourProvider>
      <ShellStyles />
      <Shell className="kshell">
        <Sidebar
          collapsed={collapsed}
          mobileOpen={mobileOpen}
          onClose={() => setMobileOpen(false)}
        />
        {/* mobile drawer backdrop */}
        <div
          className={'kside-backdrop' + (mobileOpen ? ' open' : '')}
          onClick={() => setMobileOpen(false)}
        />
        <MainColumn className="kshell-main" sx={{ ml: { xs: 0, md: `${width}px` } }}>
          <Header
            collapsed={collapsed}
            onToggle={handleToggle}
            dark={dark}
            onToggleTheme={toggleTheme}
          />
          <MainContent component="main" className="kshell-content">
            <Outlet />
          </MainContent>
        </MainColumn>
      </Shell>
    </TourProvider>
  );
};

export default SidebarLayout;
